import React, { useRef, useEffect } from 'react';
import {
  createChart,
  CandlestickSeries,
  type IChartApi,
  type CandlestickData,
  type Time,
} from 'lightweight-charts';

interface CandlestickChartData {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
}

interface CandlestickChartProps {
  data: {
    chart_data: CandlestickChartData[];
    symbol: string;
    ticker: string;
    data_points: number;
    start_date: string;
    end_date: string;
    is_intraday: boolean;
    resolution: string;
  };
}

const CandlestickChart: React.FC<CandlestickChartProps> = ({ data }) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    // Create chart with appropriate time formatting
    const chart = createChart(chartContainerRef.current, {
      autoSize: true,
      height: 400,
      layout: {
        background: { color: '#FFFFFF' },
        textColor: '#333333',
      },
      grid: {
        vertLines: { color: '#E6E6E6' },
        horzLines: { color: '#E6E6E6' },
      },
      crosshair: {
        mode: 1,
      },
      rightPriceScale: {
        borderVisible: false,
      },
      timeScale: {
        borderVisible: false,
        timeVisible: data.is_intraday, // Show time only for intraday data
      },
      localization: {
        timeFormatter: (time: any) => {
          // Handle different time formats from lightweight-charts
          let date: Date;
          if (typeof time === 'string') {
            date = new Date(time);
          } else if (typeof time === 'number') {
            // Unix timestamp (seconds or milliseconds)
            date = new Date(time > 1000000000000 ? time : time * 1000);
          } else {
            date = new Date(time);
          }

          if (isNaN(date.getTime())) {
            return String(time); // Fallback to original value
          }

          if (data.is_intraday) {
            // For intraday: convert from NY timezone to browser timezone
            const options: Intl.DateTimeFormatOptions = {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              timeZoneName: 'short'
            };
            return date.toLocaleString(undefined, options);
          } else {
            // For daily: show only date without time
            return date.toLocaleDateString();
          }
        },
      },
    });

    chartRef.current = chart;

    // ✅ v5: pass the Series *class* to addSeries
    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: false,
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
    });

    // Transform data to lightweight-charts format
    const transformedData: CandlestickData<Time>[] = data.chart_data.map(item => {
      let time: Time;

      if (data.is_intraday) {
        // For intraday data: parse timezone-aware datetime string
        const date = new Date(item.date); // Clean parsing with timezone info
        time = Math.floor(date.getTime() / 1000) as Time;
      } else {
        // For daily data: ensure date-only format (yyyy-mm-dd)
        time = item.date.split(' ')[0] as Time; // Remove time part if present
      }

      return {
        time,
        open: item.open,
        high: item.high,
        low: item.low,
        close: item.close,
      };
    });

    // Set data
    series.setData(transformedData);

    // Fit content
    chart.timeScale().fitContent();

    return () => {
      chart.remove();
      chartRef.current = null;
    };
  }, [data]);

  return (
    <div style={{ width: '100%', padding: '20px' }}>
      <div style={{ marginBottom: '16px' }}>
        <h3 style={{
          margin: '0 0 8px 0',
          fontSize: '18px',
          fontWeight: '600',
          color: '#333333'
        }}>
          {data.symbol} {data.is_intraday ? `${data.resolution} Chart` : 'Daily Chart'}
        </h3>
        <div style={{
          fontSize: '14px',
          color: '#666666',
          display: 'flex',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <span>Ticker: {data.ticker}</span>
          <span>Resolution: {data.resolution}</span>
          <span>Data Points: {data.data_points}</span>
          <span>Period: {data.start_date} to {data.end_date}</span>
        </div>
      </div>
      <div
        ref={chartContainerRef}
        style={{
          width: '100%',
          height: '400px',
          border: '1px solid #E6E6E6',
          borderRadius: '8px',
        }}
      />
    </div>
  );
};

export default CandlestickChart;